import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import BranchesTab from "@/components/settings/BranchesTab";
import RolesTab from "@/components/settings/RolesTab";
import UsersTab from "@/components/settings/UsersTab";
import InstituteTab from "@/components/settings/InstituteTab";
import AuditTab from "@/components/settings/AuditTab";
import MigrationTab from "@/components/settings/MigrationTab";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { KeyRound, Mail, Shield, User } from "lucide-react";

function ProfileTab() {
  const { user } = useAuth();
  const [pwOpen, setPwOpen] = useState(false);
  const rows = [
    { icon: User, label: "Full name", value: user?.name },
    { icon: Mail, label: "Email", value: user?.email },
    { icon: Shield, label: "Role", value: user?.role_name || user?.role, badge: true },
  ];
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3" data-testid="profile-tab">
      <div className="rounded-xl border border-border bg-card p-6 lg:col-span-2">
        <h3 className="font-display text-lg font-semibold text-secondary">Profile</h3>
        <p className="mb-5 text-sm text-muted-foreground">Your account information.</p>
        <div className="divide-y divide-border">
          {rows.map((r) => (
            <div key={r.label} className="flex items-center gap-3 py-4">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                <r.icon className="h-[18px] w-[18px]" />
              </span>
              <div className="flex-1">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{r.label}</p>
                {r.badge ? (
                  <Badge className="mt-0.5 border-0 bg-primary/10 capitalize text-primary">{r.value}</Badge>
                ) : (
                  <p className="font-medium text-secondary">{r.value}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-border bg-card p-6">
        <h3 className="font-display text-lg font-semibold text-secondary">Security</h3>
        <p className="mb-5 text-sm text-muted-foreground">Keep your account protected.</p>
        <div className="rounded-lg border border-border p-4">
          <div className="flex items-center gap-2 text-secondary">
            <KeyRound className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Password</span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">Change your password regularly for better security.</p>
          <Button className="mt-4 w-full" variant="outline" onClick={() => setPwOpen(true)} data-testid="settings-change-password">
            Change password
          </Button>
        </div>
      </div>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}

export default function Settings() {
  const { hasPermission } = useAuth();
  const showAudit = hasPermission("audit.read");
  const showMigration = hasPermission("enrollment.create");
  const showUsers = hasPermission("user.create");

  return (
    <div data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Manage your institute, branches, roles and account." />

      <Tabs defaultValue="profile">
        <TabsList className="mb-6 flex-wrap">
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
          {showUsers && <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>}
          <TabsTrigger value="institute" data-testid="tab-institute">Institute</TabsTrigger>
          <TabsTrigger value="branches" data-testid="tab-branches">Branches</TabsTrigger>
          <TabsTrigger value="roles" data-testid="tab-roles">Roles &amp; Permissions</TabsTrigger>
          {showAudit && <TabsTrigger value="audit" data-testid="tab-audit">Audit Logs</TabsTrigger>}
          {showMigration && <TabsTrigger value="migration" data-testid="tab-migration">Data Migration</TabsTrigger>}
        </TabsList>

        <TabsContent value="profile"><ProfileTab /></TabsContent>
        {showUsers && <TabsContent value="users"><UsersTab /></TabsContent>}
        <TabsContent value="institute"><InstituteTab /></TabsContent>
        <TabsContent value="branches"><BranchesTab /></TabsContent>
        <TabsContent value="roles"><RolesTab /></TabsContent>
        {showAudit && <TabsContent value="audit"><AuditTab /></TabsContent>}
        {showMigration && <TabsContent value="migration"><MigrationTab /></TabsContent>}
      </Tabs>
    </div>
  );
}
